import { DateTime } from 'luxon';

import { get } from './commonFetch';
import {
  ProcessedFlightHistory,
  RawProcessedFlightHistory,
} from '../types/flight';
import { toUTCLuxonDateTime, toZonedLuxonDateTime } from '../lib/date';

interface LatestTimezoneValues {
  departureTimeZone: {
    current?: string;
    previous?: string;
  };
  arrivalTimeZone: {
    current?: string;
    previous?: string;
  };
  divertedTimeZone: {
    current?: string;
    previous?: string;
  };
}

const parseProcessedFlightHistoryDates = (
  processedFlightHistory: RawProcessedFlightHistory,
  includeUpdates: boolean,
): ProcessedFlightHistory => {
  const updatedResponse: any = { ...processedFlightHistory };
  // this timezone store is separate because the values are updated dynamically later
  const latestTimezoneValues: LatestTimezoneValues = {
    departureTimeZone: {
      current: updatedResponse?.departureTimeZone,
    },
    arrivalTimeZone: {
      current: updatedResponse?.arrivalTimeZone,
    },
    divertedTimeZone: {
      current: updatedResponse?.divertedTimeZone,
    },
  };
  const flightTimeGroups = {
    departure: {
      names: [
        'publishedDeparture',
        'reportedGateDeparture',
        'scheduledGateDeparture',
        'actualGateDeparture',
        'estimatedGateDeparture',
        'scheduledRunwayDeparture',
        'actualRunwayDeparture',
        'estimatedRunwayDeparture',
      ],
      getTimeZone: () => latestTimezoneValues.departureTimeZone.current,
      getPreviousTimeZone: () =>
        latestTimezoneValues.departureTimeZone.previous,
    },
    nonDivertableArrival: {
      names: [
        'publishedArrival',
        'scheduledGateArrival',
        'publishedRunwayArrival',
        'scheduledRunwayArrival',
      ],
      getTimeZone: () => latestTimezoneValues.arrivalTimeZone.current,
      getPreviousTimeZone: () => latestTimezoneValues.arrivalTimeZone.previous,
    },
    // this appears to cover the logic used to select the "arrival" timeZone for diverted flights
    //   the diversion appears only to be applied to actual/estimated times
    //   (as it seems logical that planes would only be diverted after published and scheduled have been set)
    divertableArrival: {
      names: [
        'actualGateArrival',
        'reportedGateArrival',
        'estimatedGateArrival',
        'actualRunwayArrival',
        'estimatedRunwayArrival',
      ],
      getTimeZone: () =>
        latestTimezoneValues.divertedTimeZone.current ||
        latestTimezoneValues.arrivalTimeZone.current,
      getPreviousTimeZone: () =>
        latestTimezoneValues.divertedTimeZone.previous ||
        latestTimezoneValues.arrivalTimeZone.previous ||
        latestTimezoneValues.arrivalTimeZone.current,
    },
  };

  // convert top-level flight time values to luxon, or delete them if they have no relevant timeZone
  for (const flightTimeGroup in flightTimeGroups) {
    const currentGroup = flightTimeGroups[flightTimeGroup as keyof typeof flightTimeGroups];
    // conditionally convert the main datetimes individually if appropriate timeZones exist
    if (currentGroup?.getTimeZone() && currentGroup?.names) {
      currentGroup.names.forEach((timeName) => {
              const key = timeName as keyof RawProcessedFlightHistory;
              updatedResponse?.[key] &&
                (updatedResponse[key] = toZonedLuxonDateTime(
                  updatedResponse[key],
                  String(currentGroup.getTimeZone()),
                ));
            });
    } else if (currentGroup?.names) {
      // if they don't have timeZones they can't be converted anyway, so remove them from the response
      currentGroup.names.forEach(departureTimeName => {
        delete updatedResponse[departureTimeName];
      });
    }
  }

  if (includeUpdates) {
    const timeZoneNames = Object.keys(latestTimezoneValues) as (keyof LatestTimezoneValues)[];

    // reset these since we're starting at the beginning of the timeline
    delete latestTimezoneValues.departureTimeZone.current;
    delete latestTimezoneValues.arrivalTimeZone.current;
    delete latestTimezoneValues.divertedTimeZone.current;

    // Don't ADD an updates section, but if there is one, walk throught it to convert times to luxon
    updatedResponse?.updates &&
      // assumption: updates are in chronological order (oldest first) in the updates array
      (updatedResponse.updates = updatedResponse.updates.map((update: any) => {
        const revisedUpdate = { ...update };
        // convert metaData.date on the current update to
        revisedUpdate?.metaData?.date &&
          // $FlowIssue[ ignoring above check ]
          (revisedUpdate.metaData.date = toUTCLuxonDateTime(
            revisedUpdate.metaData.date,
          ));

        revisedUpdate?.irregular?.dateTime &&
          // $FlowIssue[ ignoring above check ]
          (revisedUpdate.irregular.dateTime = toUTCLuxonDateTime(
            revisedUpdate.irregular.dateTime,
          ));

        // pre-cache any timezone changes in this update for use by parsing of dates in this update
        timeZoneNames.forEach(timeZoneName => {
          // the previous values for each timeZoneName should only be available on changes that have a new timezone value
          //   otherwise changesets with ONLY time changes but not zone changes break
          delete latestTimezoneValues[timeZoneName as keyof LatestTimezoneValues].previous;
          // if the current update contains changes to the current type of timezone
          if (revisedUpdate?.changes?.[timeZoneName]) {
            // if there is a new value and an appropriate cached timezone, back up any cached timezone before writing a new one,
            //   as "old" times in this update will need to be parsed w/ the previous value
            if (
              revisedUpdate?.changes?.[timeZoneName]?.new &&
              latestTimezoneValues[timeZoneName].current
            ) {
              latestTimezoneValues[timeZoneName].previous =
                latestTimezoneValues[timeZoneName].current;
            }
            // if there is no "new" value in an update, that acts as a delete, so check for one, else the delete is done after timestamps are parsed below
            if (revisedUpdate?.changes?.[timeZoneName]?.new) {
              latestTimezoneValues[timeZoneName].current =
                revisedUpdate.changes[timeZoneName].new;
            } // else only delete below AFTER timestamps have been parsed
          }
        });
        // iterate through changes, check if each is a flight time, and convert it to luxon
        let currentChange;
        for (const changeKey in revisedUpdate.changes) {
          currentChange = revisedUpdate.changes[changeKey];
          // for flightTimeGroups (departure, nonDivertableArrival, and divertableArrival)... check if this changeKey is one fo those type of flight times
          for (const flightTimeGroup in flightTimeGroups) {
            const currentGroup = flightTimeGroups[flightTimeGroup as keyof typeof flightTimeGroups];
            if (
              currentGroup?.names?.includes(changeKey) &&
              currentGroup?.getTimeZone() // assumption: if there's a getPreviousTimeZone() result, there should be a getTimeZone() result
            ) {
              // only set this if there was already an new value (no "new" value acts as a delete)
              currentChange.new &&
                (currentChange.new = toZonedLuxonDateTime(
                  currentChange.new,
                  String(currentGroup?.getTimeZone()),
                ));
              // only set this if there was already an old value (no "old" value means set instead of update)
              currentChange.old &&
                (currentChange.old = toZonedLuxonDateTime(
                  currentChange.old,
                  String(currentGroup?.getPreviousTimeZone() ||
                    currentGroup?.getTimeZone()),
                ));
              // if we match the currentChange to one flightTimeGroup, we're not going to match another, so stop checking
              break;
            }
          }
        }
        // unset cached timezone value if unset in the flight by this update
        //   but only after it's potentially used to convert any updated timestamps
        timeZoneNames.forEach(timeZoneName => {
          if (
            revisedUpdate?.changes?.[timeZoneName] &&
            !revisedUpdate?.changes?.[timeZoneName]?.new
          ) {
            delete latestTimezoneValues[timeZoneName].current;
          }
        });
        return revisedUpdate;
      }));
  } else {
    // don't leave unparsed updates on the response
    delete updatedResponse?.updates;
  }
  updatedResponse?.creationDate &&
    (updatedResponse.creationDate = toUTCLuxonDateTime(
      updatedResponse.creationDate,
    ));

  return updatedResponse;
};

// to Debug curl https://trips-search-api-staging.fsadmin.cirium.dev/internal/processedFlightHistory/:flightId
export const getFlight = (
  flightId: string,
): Promise<ProcessedFlightHistory> => {
  return get(`/api/flights/${flightId}`).then(processedFlightHistory => {
    return parseProcessedFlightHistoryDates(processedFlightHistory as RawProcessedFlightHistory, true);
  });
};

export const flightSearch = (
  airlineIata: string,
  flightNumber: string,
  departureDate?: DateTime,
  arrivalDate?: DateTime,
  departureAirportIata?: string,
  arrivalAirportIata?: string,
  status?: string,
): Promise<ProcessedFlightHistory[]> => {
  const queryParams = {
    airlineIata,
    flightNumber,
    departureDate,
    arrivalDate,
    departureAirportIata,
    arrivalAirportIata,
    status,
  };

  return get('/api/flights/search', queryParams).then((resp) => {
    const response = resp as RawProcessedFlightHistory[];
    const updatedResp = [...response];
    if (updatedResp?.map) {
      return updatedResp.map(processedFlightHistory =>
        parseProcessedFlightHistoryDates(processedFlightHistory, false),
      );
    } else {
      // just in case there's a case where there's no data, but also no response error
      return [];
    }
  });
};
